// Firmark Calcs — Slice 0: the PURE adapter between the imported design's
// request/result model and the real Firmark calc engine.
//
// Two pure functions:
//   buildCalcInputs(req)  — DesignInputs → engine flat input dict (capability-
//                           gated first; returns {unsupported, reasons} rather
//                           than fabricating a request the engine can't back).
//   toResultModel(...)    — engine CalcResult → the design's `M.*` ResultModel.
//
// ===========================================================================
// HARD INVARIANTS (do not violate — these keep a stamped calc honest):
//
//   INV-1  No client structural math for any LABELED number. The diagram curve
//          arrays are SHAPE-ONLY (simple-uniform analytic shape) and may NEVER
//          source a labeled magnitude / DCR / factor; the labeled maxima are
//          always the engine's (structured.diagram / reactions).
//   INV-2  An absent factor or adjusted stress is `null`, NEVER derived client-
//          side. CD/lambda is read per-combo from the engine; every other factor /
//          primed stress is read from the engine-authored structured.factors /
//          structured.adjustedStresses (record.intermediates is a legacy
//          fallback), else null.
//   INV-3  DB provenance is asserted ONLY from reference.source === "member_db".
//          Anything else is "direct" (typed hand calc).
//   INV-4  The standard / edition string comes from the engine result at
//          runtime (structured.ndsEdition) — never a hardcoded "NDS 2018" etc.
//   INV-5  Tributary width == spacing only. A manual tributary that differs
//          from spacing is gated unsupported (never silently reinterpreted).
// ===========================================================================

import type {
  CalcResult,
  CalcInputs,
  CalcStructured,
  CalcSummaryRow,
  CalcReaction,
  CalcDiagramCombo,
  CalcSupportInput,
  CalcLoadInput,
} from "../../types/calc";
import type {
  DesignInputs,
  DesignLoad,
  EngineReq,
  Provenance,
  ResultCheck,
  ResultComboResult,
  ResultComboTable,
  ResultDiagram,
  ResultCriteria,
  ResultFactor,
  ResultFactors,
  ResultMat,
  ResultModel,
  ResultReaction,
  ResultRefs,
} from "./ndsTypes";
import { NDS_ROLES } from "./ndsRoles";
import { prettyUnits } from "./calcDerive";

// ---------------------------------------------------------------------------
// buildCalcInputs — capability gate + engine flat-input mapping
// ---------------------------------------------------------------------------

export type BuildOk = { calculatorId: string; inputs: CalcInputs };
export type BuildUnsupported = { unsupported: true; reasons: string[] };
export type BuildResult = BuildOk | BuildUnsupported;

// The engine stress scalars, in canonical order (matches _wood_reference.STRESS_KEYS).
const STRESS_KEYS = [
  "fb_psi", "fv_psi", "fc_perp_psi", "e_psi", "emin_psi", "ft_psi", "fc_psi",
] as const;
type StressKey = (typeof STRESS_KEYS)[number];

// Stable gate-reason strings (match engine-gaps.md — do NOT reword).
const GATE = {
  roleBeamCol:
    "engine support pending: combined axial + bending (beam-column). The wood " +
    "column calculator is axial-only; a stud/beam-column with transverse wind " +
    "needs NDS 3.9.2 interaction.",
  studLateral:
    "engine support pending: non-wind lateral load on a stud wall. The stud " +
    "beam-column carries out-of-plane WIND pressure (psf) only.",
  biaxialPressure:
    "the biaxial beam-column takes APPLIED moments (M1 strong / M2 weak, " +
    "lb·ft per ASCE type), not a lateral pressure. Enter the moment, or use " +
    "the Stud (wall) template for a pressure-driven member.",
  align:
    "engine support pending: projected/along-member load alignment for sloped " +
    "members.",
  manualTrib:
    "engine support pending: manual tributary width different from member spacing.",
  glulamKey:
    "glulam design values are combination-specific — select a glulam section " +
    "from the member database (the picker's Glulam family). Typed glulam " +
    "values are not accepted (the NDS 5.3.6 volume factor needs the catalog " +
    "section).",
  ijoistCapacity:
    "an I-joist is checked on the manufacturer's published capacities — " +
    "enter M_r, V_r and EI (from the product literature), or pick a catalog " +
    "I-joist.",
  ijoistLoads:
    "the I-joist calculator carries uniform gravity loads (D / L / Lr / S " +
    "psf) on a single simple span — wind, point / partial / tapered loads, " +
    "multi-bearing supports, cantilevers and slope are out of its published-" +
    "capacity scope. Switch the material to sawn / LVL for rich loading.",
  ijoistConditions:
    "the I-joist calculator uses manufacturer-published capacities and EI/K " +
    "for the documented service condition; wet, elevated-temperature, and " +
    "incised adjustments are not derived from sawn-lumber factors, and its " +
    "current contract exposes live/total deflection limits only.",
  weakMaterial:
    "weak-axis uniaxial design is currently limited to single-ply sawn " +
    "dimension lumber with a transcribed NDS flat-use factor. Engineered " +
    "products require their product-specific weak-axis design values.",
  weakBuiltUp:
    "weak-axis uniaxial design requires one ply; built-up plies are not " +
    "assumed composite through their wide faces.",
  shearMaterial:
    "direct shear deformation currently uses the sawn-lumber Appendix F " +
    "stiffness split. Engineered products require their published EI/K or " +
    "product-specific bending and shear stiffness.",
  incisedMaterial:
    "the NDS incising factor in this calculator is limited to sawn lumber; " +
    "engineered products require their manufacturer treatment data.",
} as const;

function num(v: unknown, dflt = 0): number {
  if (v === null || v === undefined || v === "") return dflt;
  const n = typeof v === "number" ? v : parseFloat(String(v));
  return Number.isFinite(n) ? n : dflt;
}

function woodConditionInputs(vals: DesignInputs): CalcInputs {
  return {
    moisture_service: vals.service === "wet" ? "wet" : "dry",
    temperature_class: vals.temp ?? "le_100F",
    incised: vals.incised ?? false,
  };
}

// Is this distributed load full-span? start at (or before) 0 and end at (or
// past) span, with a small tolerance. A load with no start/end is treated as
// full span (the design's default for a fresh distributed load is start=0,
// end=span). loadKind "line" carries plf with no surface extent — treated full.
function isFullSpan(l: DesignLoad, spanFt: number): boolean {
  const s = num(l.start, 0);
  const e = l.end === undefined || l.end === null ? spanFt : num(l.end, spanFt);
  return s <= 0.01 && (e <= 0.01 || Math.abs(e - spanFt) < 0.02 || e >= spanFt - 0.02);
}

// Aggregate the design's per-source loads into the engine's psf/plf/lb keys.
// `kind` selects the destination unit family.
function aggregateLoads(
  loads: DesignLoad[],
  kind: "area" | "line" | "axial",
): Record<string, number> {
  const out: Record<string, number> = {};
  const add = (key: string, v: number) => {
    out[key] = (out[key] ?? 0) + v;
  };
  if (kind === "axial") {
    for (const l of loads) {
      const mag = num(l.mag);
      switch (l.type) {
        case "D": add("dead_lb", mag); break;
        case "L": add("floor_live_lb", mag); break;
        case "Lr": add("roof_live_lb", mag); break;
        case "S": add("snow_lb", mag); break;
        // W (axial wind) has no dedicated *_lb bucket in the axial calcs; the
        // tension preset routes wind tension through the gravity sources. A bare
        // axial W is intentionally not mapped here (no engine key) — left out.
        default: break;
      }
    }
    return out;
  }
  // area → psf, line → plf, both land in the same *_psf engine keys (the line
  // path sets spacing_in = 12 so psf×trib = the entered plf — see buildCalcInputs).
  for (const l of loads) {
    const mag = num(l.mag);
    switch (l.type) {
      case "D": add("dead_psf", mag); break;
      case "L": add("floor_live_psf", mag); break;
      case "Lr": add("roof_live_psf", mag); break;
      case "S": add("snow_psf", mag); break;
      case "R": add("rain_psf", mag); break;
      case "W": add(l.uplift ? "wind_up_psf" : "wind_down_psf", mag); break;
      default: break;
    }
  }
  return out;
}

// ASCE source -> the engine's load-type string (W splits to wind_down/wind_up
// by the design's uplift hint).
const LOAD_TYPE_OUT: Record<string, string> = {
  D: "dead",
  L: "floor_live",
  Lr: "roof_live",
  S: "snow",
  R: "rain",
};

// Translate one design beam load into the engine's typed-load contract. A
// distributed load with no start/end is full-span (the engine defaults the
// extent to the member length); a start/end makes it partial; `point` makes it
// concentrated. Tributary: area roles send psf (×spacing in the engine); line
// roles send plf; point loads send lb.
function translateLoad(l: DesignLoad, lineRole: boolean, spanFt: number): CalcLoadInput {
  const type =
    l.type === "W" ? (l.uplift ? "wind_up" : "wind_down") : LOAD_TYPE_OUT[l.type] ?? "dead";
  if (l.point) {
    return {
      type,
      distribution: "point",
      magnitude: num(l.mag),
      unit: "lb",
      position_ft: num(l.pos),
    };
  }
  const out: CalcLoadInput = {
    type,
    distribution: l.taper ? "tapered" : "udl",
    magnitude: num(l.mag),
    unit: lineRole ? "plf" : "psf",
  };
  if (l.start !== undefined && l.start !== null && num(l.start) > 0) out.start_ft = num(l.start);
  if (l.end !== undefined && l.end !== null) out.end_ft = num(l.end);
  if (l.taper) out.w_end = num(l.magEnd, num(l.mag));
  // spanFt is the fallback full extent; left to the engine when start/end absent.
  void spanFt;
  return out;
}

// Emit the design-value scalars + the design_value_overrides array per PR #184:
//   source === "Database": send NO stress scalar for any cell NOT overridden;
//     for overridden cells send the typed scalar AND list the key in
//     design_value_overrides (keeps the engine's DB badge truthful).
//   source === "Custom": send EVERY relevant scalar, NO overrides.
// `relevant` is the subset of stress keys this calculator reads.
function emitDesignValues(
  vals: DesignInputs,
  relevant: readonly StressKey[],
  inputs: CalcInputs,
): void {
  const source = vals.source ?? "Database";
  if (source === "Custom") {
    for (const k of relevant) {
      const v = vals[k];
      if (v !== undefined && v !== null) inputs[k] = num(v);
    }
    return;
  }
  // Database: only overridden cells get a scalar, and they get listed.
  const overrides = (vals.designValueOverrides ?? []).filter(
    (k): k is StressKey => (STRESS_KEYS as readonly string[]).includes(k),
  );
  const applied: string[] = [];
  for (const k of relevant) {
    if (overrides.includes(k)) {
      const v = vals[k];
      if (v !== undefined && v !== null) {
        inputs[k] = num(v);
        applied.push(k);
      }
    }
  }
  if (applied.length) {
    // canonical order
    inputs.design_value_overrides = STRESS_KEYS.filter((k) => applied.includes(k));
  }
}

export function buildCalcInputs(req: EngineReq): BuildResult {
  const role = NDS_ROLES[req.role];
  const reasons: string[] = [];

  // --- capability gate FIRST (do not fabricate a request) ------------------
  if (!role || !role.supported || role.calculatorId === null) {
    reasons.push(GATE.roleBeamCol);
    return { unsupported: true, reasons };
  }

  const vals = req.vals ?? {};
  const designMethod = req.method ?? vals.designMethod ?? "ASD";
  const liveDurationCategory = vals.liveDurationCategory ?? "occupancy";
  const loads = vals.loads ?? [];
  const calculatorId = role.calculatorId;

  if (role.kind === "bending") {
    const supports = vals.supports ?? [];
    const sortedSup = [...supports].sort((a, b) => num(a.pos) - num(b.pos));
    const spanFt = num(vals.span);
    const spacingIn = num(vals.spacing);

    // Gates that still stand (out of scope for the multi-bearing engine pass):
    // non-gravity load alignment and a manual tributary that differs from spacing.
    // (The old single-simple-span multiSpan / pointPartial gates are retired —
    // the engine's general FE beam now backs multi-bearing / cantilever / point /
    // partial / tapered loads; see the FE routing below.)
    for (const l of loads) {
      if (l.axial || l.lateral) continue; // not a beam gravity load
      const align = l.align ?? "gravity";
      if (align !== "gravity" && !reasons.includes(GATE.align)) {
        reasons.push(GATE.align);
      }
      if (l.trib !== undefined && l.trib !== null && role.loadKind === "area") {
        const tribIn = num(l.trib) * 12;
        if (Math.abs(tribIn - spacingIn) > 0.01 && !reasons.includes(GATE.manualTrib)) {
          reasons.push(GATE.manualTrib);
        }
      }
    }
    if (reasons.length) return { unsupported: true, reasons };

    const lineRole = role.loadKind === "line";
    const beamLoads = loads.filter((l) => !l.axial && !l.lateral);

    // --- MATERIAL DISPATCH (founder model): the template's material selector
    //     routes to the right engine calc — sawn/glulam → wood_beam_asd,
    //     LVL/LSL/PSL → wood_scl_beam_asd, I-joist → wood_ijoist_asd. --------
    const material = String(vals.member_family ?? "sawn").toLowerCase();
    const orientation = vals.orientation ?? "strong";
    if (orientation === "weak" && material !== "sawn") {
      return { unsupported: true, reasons: [GATE.weakMaterial] };
    }
    if (orientation === "weak" && Math.max(1, Math.round(num(vals.plies, 1))) !== 1) {
      return { unsupported: true, reasons: [GATE.weakBuiltUp] };
    }
    if (vals.shearDeflection && material !== "sawn") {
      return { unsupported: true, reasons: [GATE.shearMaterial] };
    }
    if (vals.incised && material !== "sawn") {
      return { unsupported: true, reasons: [GATE.incisedMaterial] };
    }

    if (material === "ijoist") {
      if (vals.service === "wet" || (vals.temp && vals.temp !== "le_100F") ||
          vals.incised || vals.deflLT !== undefined || num(vals.deflAbs) > 0 ||
          vals.kcr !== undefined) {
        return { unsupported: true, reasons: [GATE.ijoistConditions] };
      }
      // capacity-tabulated check: uniform gravity psf on a single simple span.
      const rich = beamLoads.some((l) => l.point || l.taper || !isFullSpan(l, spanFt));
      const hasWind = beamLoads.some((l) => l.type === "W");
      if (rich || hasWind || supports.length > 0 ||
          num(vals.cantilever_left_ft) > 0 || num(vals.cantilever_right_ft) > 0 ||
          num(vals.slope) > 0) {
        return { unsupported: true, reasons: [GATE.ijoistLoads] };
      }
      const hasKey = !!vals.i_joist_member_key;
      const hasTyped =
        num(vals.ijoist_mr_ftlb) > 0 && num(vals.ijoist_vr_lb) > 0 &&
        num(vals.ijoist_ei_lb_in2) > 0;
      if (!hasKey && !hasTyped) {
        return { unsupported: true, reasons: [GATE.ijoistCapacity] };
      }
      const inputs: CalcInputs = {
        design_method: designMethod,
        live_duration_category: liveDurationCategory,
        member_id: vals.mark ?? role.mark,
        span_ft: spanFt,
        spacing_in: spacingIn,
        include_self_weight: vals.selfWeight ?? false,
        defl_live_ratio: num(vals.deflLL, 360),
        defl_total_ratio: num(vals.deflTL, 240),
        ...aggregateLoads(beamLoads, "area"),
        ...(hasKey
          ? { i_joist_member_key: vals.i_joist_member_key as string }
          : {
              mr_ftlb: num(vals.ijoist_mr_ftlb),
              vr_lb: num(vals.ijoist_vr_lb),
              ei_lb_in2: num(vals.ijoist_ei_lb_in2),
              k_lb: num(vals.ijoist_k_lb),
              reaction_lb: num(vals.ijoist_reaction_lb),
              kf_moment: num(vals.ijoist_kf_moment),
              kf_shear: num(vals.ijoist_kf_shear),
              kf_reaction: num(vals.ijoist_kf_reaction),
              lrfd_capacity_basis: vals.ijoist_lrfd_capacity_basis ?? "",
              weight_plf: num(vals.ijoist_weight_plf),
              manufacturer: vals.ijoist_manufacturer ?? "",
              series: vals.ijoist_series ?? "",
            }),
      };
      return { calculatorId: "wood_ijoist_asd", inputs };
    }

    if (material === "glulam" && !vals.engineered_member_key) {
      // glulam rides wood_beam_asd via the catalog lookup_key (CV applies);
      // there is no honest typed-glulam path — gate until a section is picked.
      return { unsupported: true, reasons: [GATE.glulamKey] };
    }
    const isScl = material === "lvl" || material === "lsl" || material === "psl";
    const dispatchId = isScl ? "wood_scl_beam_asd" : calculatorId;
    // A single simple span under full-span uniform loads keeps the original
    // flat contract (closed-form engine path; preserves the ClearCalcs parity
    // anchors). A supports table, a cantilever, or any point / partial /
    // tapered load routes to the general FE contract.
    const hasSupportsTable = supports.length > 0;
    const hasCantilever =
      num(vals.cantilever_left_ft) > 0 || num(vals.cantilever_right_ft) > 0;
    const richLoads = beamLoads.some(
      (l) => l.point || l.taper || !isFullSpan(l, spanFt),
    );
    const useFe = hasSupportsTable || hasCantilever || richLoads || !!vals.shearDeflection;

    const inputs: CalcInputs = {
      design_method: designMethod,
      live_duration_category: liveDurationCategory,
      member_id: vals.mark ?? role.mark,
      callout: vals.size ?? "",
      b_in: num(vals.b_in),
      d_in: num(vals.d_in),
      plies: Math.max(1, Math.round(num(vals.plies, 1))),
      species: vals.species ?? "",
      grade: vals.grade ?? "",
      // Engineered (glulam): the lookup_key routes the adapter to the
      // engineered reference (CV applies); absent for sawn members.
      ...(vals.engineered_member_key
        ? { engineered_member_key: vals.engineered_member_key }
        : {}),
      // SCL (LVL/LSL/PSL): dispatched to wood_scl_beam_asd — the family drives
      // the NDS 8.3.6 depth/volume factor + material-gated Cr (8.3.7).
      ...(isScl ? { scl_family: material.toUpperCase() } : {}),
      ...woodConditionInputs(vals),
      // line roles enter plf: spacing_in = 12 so psf×trib(1ft) == the entered
      // plf. area roles pass psf through with the real spacing.
      spacing_in: lineRole ? 12 : spacingIn,
      bearing_length_in: num(vals.bearing, 3.0),
      // per-end bearing length (left/right by position) for asymmetric bearing;
      // falls back to the single bearing when no supports were entered.
      bearing_length_left_in:
        sortedSup.length === 2
          ? num(sortedSup[0].bearing, num(vals.bearing, 3.0))
          : num(vals.bearing, 3.0),
      bearing_length_right_in:
        sortedSup.length === 2
          ? num(sortedSup[1].bearing, num(vals.bearing, 3.0))
          : num(vals.bearing, 3.0),
      slope_rise: num(vals.slope, 0),
      slope_run: 12,
      top_braced: (vals.bracing ?? "continuous") === "continuous",
      bracing_pattern: vals.bracing ?? "continuous",
      top_unbraced_length_in: spanFt * 12, // only consulted when not top_braced
      include_self_weight: vals.selfWeight ?? false,
      repetitive: vals.repetitive ?? false,
      member_orientation: orientation,
      include_shear_deformation: vals.shearDeflection ?? false,
      defl_live_ratio: num(vals.deflLL, 360),
      defl_total_ratio: num(vals.deflTL, 240),
      defl_long_term_ratio: num(vals.deflLT, num(vals.deflTL, 240)),
      defl_absolute_cap_in: num(vals.deflAbs, 0),
      kcr: num(vals.kcr, vals.service === "wet" ? 2.0 : 1.5),
    };

    if (useFe) {
      // --- general FE contract: N bearings + cantilevers + typed loads -----
      if (hasSupportsTable) {
        const sorted = [...supports].sort((a, b) => num(a.pos) - num(b.pos));
        inputs.supports = sorted.map(
          (s): CalcSupportInput => ({
            pos_ft: num(s.pos),
            bearing_in: num(s.bearing, num(vals.bearing, 3.0)),
            fixed: s.type === "fixed",
          }),
        );
        // member length (the right tip) drives the right cantilever; the left
        // overhang is the first support's position (member starts at x=0).
        if (num(vals.member_length_ft) > 0) {
          inputs.member_length_ft = num(vals.member_length_ft);
        }
        const rightOh = num(sorted[sorted.length - 1]?.overhang, 0);
        if (rightOh > 0) inputs.cantilever_right_ft = rightOh;
      } else {
        // single span + cantilever overhang(s)
        inputs.span_ft = spanFt;
        if (num(vals.cantilever_left_ft) > 0) {
          inputs.cantilever_left_ft = num(vals.cantilever_left_ft);
        }
        if (num(vals.cantilever_right_ft) > 0) {
          inputs.cantilever_right_ft = num(vals.cantilever_right_ft);
        }
      }
      inputs.loads = beamLoads.map((l) => translateLoad(l, lineRole, spanFt));
    } else {
      // --- simple full-span path: the original flat psf/plf contract --------
      inputs.span_ft = spanFt;
      Object.assign(inputs, aggregateLoads(beamLoads, lineRole ? "line" : "area"));
    }

    emitDesignValues(vals, ["fb_psi", "fv_psi", "fc_perp_psi", "e_psi", "emin_psi"], inputs);
    return { calculatorId: dispatchId, inputs };
  }

  if (role.kind === "beamcol" && calculatorId === "wood_biaxial_beam_column_asd") {
    // beam_column → wood_biaxial_beam_column_asd: applied per-type axial force
    // + strong (M1) / weak (M2) moments through the NDS 3.9 interaction. Its
    // vocabulary is applied ACTIONS — a lateral pressure row is gated (the
    // client will never derive a moment from a pressure; INV-1).
    if (loads.some((l) => l.lateral)) {
      reasons.push(GATE.biaxialPressure);
    }
    if (reasons.length) return { unsupported: true, reasons };

    // per-ASCE-type engine keys: axial_<src>_lb / m1_<src>_lbft / m2_<src>_lbft
    const SRC: Record<string, string> = {
      D: "dead", L: "floor_live", Lr: "roof_live", S: "snow", W: "wind",
    };
    const perType: Record<string, number> = {};
    for (const l of loads) {
      const src = SRC[l.type];
      if (!src) continue;
      const mag = num(l.mag);
      if (!mag) continue;
      const key = l.momentAxis
        ? `${l.momentAxis === "M2" ? "m2" : "m1"}_${src}_lbft`
        : l.axial
          ? `axial_${src}_lb`
          : null;
      if (key) perType[key] = (perType[key] ?? 0) + mag;
    }

    const inputs: CalcInputs = {
      design_method: designMethod,
      live_duration_category: liveDurationCategory,
      member_id: vals.mark ?? role.mark,
      callout: vals.size ?? "",
      b_in: num(vals.b_in),
      d_in: num(vals.d_in),
      plies: 1, // the engine refuses a built-up biaxial member
      species: vals.species ?? "",
      grade: vals.grade ?? "",
      ...woodConditionInputs(vals),
      height_ft: num(vals.height),
      ke: num(vals.ke, 1.0),
      // compression-edge bracing for strong-axis CL: the design's bracing
      // select ("continuous" = braced) → lateral_braced; default unbraced.
      lateral_braced: (vals.bracing ?? "unbraced") === "continuous",
      // end bearing on the plates: the entered seat, else the engine defaults
      // to the full section.
      ...(num(vals.bearing) > 0 ? { bearing_length_in: num(vals.bearing) } : {}),
      ...perType,
    };
    emitDesignValues(
      vals,
      ["fc_psi", "fb_psi", "fv_psi", "fc_perp_psi", "ft_psi", "e_psi", "emin_psi"],
      inputs,
    );
    return { calculatorId, inputs };
  }

  if (role.kind === "beamcol") {
    // stud → wood_stud_wall_asd: a sheathed repetitive stud as a beam-column
    // (gravity axial + out-of-plane wind, NDS 3.9.2). Dispatch by calculatorId
    // so the biaxial beam-column can join this branch with its own mapping.
    if (calculatorId !== "wood_stud_wall_asd") {
      reasons.push(GATE.roleBeamCol);
      return { unsupported: true, reasons };
    }
    // lateral loads: the engine key is a WIND wall pressure (psf). Any other
    // lateral source has no engine bucket — gate, never silently drop. An
    // applied-moment row (biaxial vocabulary) has no stud-wall key either.
    if (loads.some((l) => l.lateral && l.type !== "W")) {
      reasons.push(GATE.studLateral);
    }
    if (loads.some((l) => l.momentAxis)) {
      reasons.push(GATE.biaxialPressure);
    }
    if (reasons.length) return { unsupported: true, reasons };

    const windPsf = loads
      .filter((l) => l.lateral && l.type === "W")
      .reduce((sum, l) => sum + num(l.mag), 0);
    const inputs: CalcInputs = {
      design_method: designMethod,
      live_duration_category: liveDurationCategory,
      member_id: vals.mark ?? role.mark,
      callout: vals.size ?? "",
      b_in: num(vals.b_in),
      d_in: num(vals.d_in),
      plies: 1, // single-ply stud (the engine refuses built-up studs)
      species: vals.species ?? "",
      grade: vals.grade ?? "",
      ...woodConditionInputs(vals),
      height_ft: num(vals.height),
      spacing_in: num(vals.spacing, 16),
      ke: num(vals.ke, 1.0),
      // the template's premise: a sheathed wall — weak axis braced, repetitive
      // Cr on Fb. Both explicit so the conservative bare defaults never bite.
      braced_minor_axis: true,
      repetitive: vals.repetitive ?? true,
      wind_psf: windPsf,
      include_self_weight: vals.selfWeight ?? false,
      defl_live_ratio: num(vals.deflLL, 240),
      defl_total_ratio: num(vals.deflTL, 180),
      ...aggregateLoads(loads.filter((l) => l.axial), "axial"),
    };
    emitDesignValues(
      vals,
      ["fc_psi", "fb_psi", "fv_psi", "fc_perp_psi", "e_psi", "emin_psi"],
      inputs,
    );
    return { calculatorId, inputs };
  }

  if (role.kind === "axial") {
    // post → wood_column_asd. (tension is role.kind === "tension" below.)
    // Axial loads: any point/lateral/non-axial flagged loads are not the axial
    // model — but the design's axial roles carry axial:true loads, so a lateral
    // load present on an axial role would mean beam-column → gate it.
    if (loads.some((l) => l.lateral)) {
      reasons.push(GATE.roleBeamCol);
    }
    if (reasons.length) return { unsupported: true, reasons };

    const inputs: CalcInputs = {
      design_method: designMethod,
      live_duration_category: liveDurationCategory,
      member_id: vals.mark ?? role.mark,
      callout: vals.size ?? "",
      b_in: num(vals.b_in),
      d_in: num(vals.d_in),
      plies: Math.max(1, Math.round(num(vals.plies, 1))),
      species: vals.species ?? "",
      grade: vals.grade ?? "",
      ...woodConditionInputs(vals),
      height_ft: num(vals.height),
      ke: num(vals.ke, 1.0),
      braced_minor_axis: false, // a post is unbraced both axes (design default)
      include_self_weight: vals.selfWeight ?? false,
      ...aggregateLoads(loads.filter((l) => l.axial), "axial"),
    };
    emitDesignValues(
      vals,
      ["fc_psi", "fb_psi", "fv_psi", "fc_perp_psi", "e_psi", "emin_psi"],
      inputs,
    );
    return { calculatorId, inputs };
  }

  // role.kind === "tension" → wood_tension_asd
  if (loads.some((l) => l.lateral)) {
    reasons.push(GATE.roleBeamCol);
  }
  if (reasons.length) return { unsupported: true, reasons };

  const inputs: CalcInputs = {
    design_method: designMethod,
    live_duration_category: liveDurationCategory,
    member_id: vals.mark ?? role.mark,
    callout: vals.size ?? "",
    b_in: num(vals.b_in),
    d_in: num(vals.d_in),
    plies: Math.max(1, Math.round(num(vals.plies, 1))),
    species: vals.species ?? "",
    grade: vals.grade ?? "",
    ...woodConditionInputs(vals),
    length_ft: num(vals.height),
    ...aggregateLoads(loads.filter((l) => l.axial), "axial"),
  };
  emitDesignValues(
    vals,
    ["ft_psi", "fb_psi", "fv_psi", "fc_perp_psi", "e_psi", "emin_psi"],
    inputs,
  );
  return { calculatorId, inputs };
}

// ---------------------------------------------------------------------------
// toResultModel — engine CalcResult → design M.*
// ---------------------------------------------------------------------------

// summary.key → design check key.
const CHECK_KEY_MAP: Record<string, string> = {
  flexure: "bending",
  bending: "bending",
  shear: "shear",
  bearing: "bearing",
  deflection_live: "deflLL",
  deflection_total: "deflTL",
  deflection_long_term: "deflLT",
  compression: "compression",
  tension: "tension",
  combined_bending_axial: "combined",
  biaxial_bending_axial: "combined",
  ijoist_moment: "bending",
  ijoist_shear: "shear",
  ijoist_reaction: "bearing",
  ijoist_deflection_live: "deflLL",
  ijoist_deflection_total: "deflTL",
};

function mapKey(k: string): string {
  return CHECK_KEY_MAP[k] ?? k;
}

// qualitative location — never a fabricated coordinate.
function deriveLoc(key: string): string {
  if (key === "bending" || key === "deflLL" || key === "deflTL" || key === "deflLT")
    return "midspan";
  if (key === "shear") return "face of support";
  if (key === "bearing") return "support";
  return "";
}

function fmt(v: number | null | undefined): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return "—";
  const n = Math.abs(v) >= 100 ? Math.round(v) : Math.round(v * 100) / 100;
  return n.toLocaleString();
}

function synthEquation(s: CalcSummaryRow): string {
  const d = s.demandSymbol || "demand";
  const c = s.capacitySymbol || "capacity";
  return `${d} / ${c}`;
}

// Read a numeric factor from a record-intermediates bag IF present, else null.
// NEVER computes a factor. The bag is res.record.intermediates merged with each
// limit-state's intermediates (the engine stores factors keyed e.g. "CL","CF").
function readFactor(bag: Record<string, unknown> | null, ...keys: string[]): number | null {
  if (!bag) return null;
  for (const k of keys) {
    const v = bag[k];
    if (typeof v === "number" && Number.isFinite(v)) return v;
  }
  return null;
}

// Build a single flat bag of intermediates from res.record (if present). The
// engine record carries per-limit-state intermediates plus possibly top-level
// ones; we shallow-merge them so readFactor can find CL/CF/CM/etc. wherever the
// engine put them. Absent record → null (so every parsed factor is null).
function collectIntermediates(
  record: Record<string, unknown> | undefined,
): Record<string, unknown> | null {
  if (!record) return null;
  const bag: Record<string, unknown> = {};
  const top = record.intermediates;
  if (top && typeof top === "object") Object.assign(bag, top as Record<string, unknown>);
  // limit states may live under record.limit_states / record.checks
  for (const listKey of ["limit_states", "checks", "limitStates"]) {
    const list = record[listKey];
    if (Array.isArray(list)) {
      for (const ls of list) {
        if (ls && typeof ls === "object") {
          const inter = (ls as Record<string, unknown>).intermediates;
          if (inter && typeof inter === "object") {
            Object.assign(bag, inter as Record<string, unknown>);
          }
        }
      }
    }
  }
  return Object.keys(bag).length ? bag : null;
}

function objectValue(
  source: Record<string, unknown> | undefined,
  key: string,
): Record<string, unknown> | null {
  const value = source?.[key];
  return value && typeof value === "object" && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null;
}

function numericValue(
  source: Record<string, unknown> | null,
  key: string,
): number | null {
  const value = source?.[key];
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function stringValue(
  source: Record<string, unknown> | null,
  key: string,
): string | null {
  const value = source?.[key];
  return typeof value === "string" && value.length > 0 ? value : null;
}

function booleanValue(
  source: Record<string, unknown> | null,
  key: string,
): boolean | null {
  const value = source?.[key];
  return typeof value === "boolean" ? value : null;
}

// Simple-uniform analytic SHAPE arrays for the diagram curves (INV-1: geometry
// only; these may never source a labeled magnitude). V(x)=w(L/2−x),
// M(x)=wx(L−x)/2, δ via 5wL⁴/384EI normalized shape. w is unit (1) — the curve
// is scaled by the renderer to the engine's labeled maxima, never the reverse.
function shapeArrays(spanIn: number | null): {
  xs: number[];
  V: number[];
  M: number[];
  defl: number[];
} {
  const N = 40;
  const L = spanIn && spanIn > 0 ? spanIn : 1;
  const w = 1; // SHAPE ONLY — magnitude is meaningless; never read as a value.
  const xs: number[] = [];
  const V: number[] = [];
  const M: number[] = [];
  const defl: number[] = [];
  for (let i = 0; i <= N; i++) {
    const x = (i / N) * L;
    xs.push(x);
    V.push(w * (L / 2 - x));
    M.push((w * x * (L - x)) / 2);
    // deflection shape of a simply-supported uniform beam (unit EI): peaks at mid
    defl.push((w * x * (L ** 3 - 2 * L * x * x + x ** 3)) / 24);
  }
  return { xs, V, M, defl };
}

export function toResultModel(
  _calculatorId: string,
  res: CalcResult,
  req: EngineReq,
): ResultModel {
  const s: CalcStructured = res.structured;
  const ref = s.reference ?? {};
  const record = res.record;
  const bag = collectIntermediates(record);
  const recordInputs = objectValue(record, "inputs");
  const service = objectValue(recordInputs ?? undefined, "service");
  const serviceability = objectValue(recordInputs ?? undefined, "serviceability");
  const bracing = objectValue(recordInputs ?? undefined, "bracing");

  // --- section / material (engine reference only) --------------------------
  const mat: ResultMat = {
    Fb: ref.fb ?? null,
    Fv: ref.fv ?? null,
    Fcp: ref.fcPerp ?? null,
    Fc: ref.fc ?? null,
    Ft: ref.ft ?? null,
    E: ref.e ?? null,
    Emin: ref.emin ?? null,
    kind: null, // engine does not emit a material "kind" — never derive (INV-2)
    Kcr: null, // never derive a structural constant
  };

  // --- checks (from structured.summary; filter rows with null demand) -------
  const checksBySummaryKey = new Map<string, CalcSummaryRow>();
  const structuredChecks = s.checks ?? [];
  const checkByState = new Map(structuredChecks.map((c) => [c.limitState, c]));

  const checks: ResultCheck[] = (s.summary ?? [])
    .filter((row) => row.demand !== null && row.demand !== undefined)
    .map((row) => {
      checksBySummaryKey.set(row.key, row);
      const dk = mapKey(row.key);
      const ec = checkByState.get(row.key);
      return {
        key: dk,
        name: row.label,
        dc: row.utilization,
        // Display strings typeset the units (lb·ft, in²) at this presentation
        // boundary - the engine row's raw units value is untouched.
        demand: `${fmt(row.demand)} ${prettyUnits(row.units)}`.trim(),
        capacity: `${fmt(row.capacity)} ${prettyUnits(row.units)}`.trim(),
        equation: synthEquation(row),
        ref: ec?.ndsRef ?? null,
        combo: row.governingCombo,
        loc: deriveLoc(dk),
        details: ec?.intermediates ?? {},
        threshold: ec?.threshold ?? null,
      };
    });

  // --- reactions ------------------------------------------------------------
  const reactions: ResultReaction[] = (s.reactions ?? []).map((r: CalcReaction) => ({
    support: r.label,
    value: `${fmt(r.maxLb)} lb`,
    tag: s.governing ?? "",
    minBearing: r.minBearingLenIn != null ? `${fmt(r.minBearingLenIn)} in` : null,
  }));

  // --- governing check ------------------------------------------------------
  let gov: ResultCheck | null = null;
  if (s.governing) {
    const govKey = mapKey(s.governing);
    gov = checks.find((c) => c.key === govKey) ?? null;
  }
  if (!gov && checks.length) {
    gov = checks.reduce((a, b) => ((b.dc ?? -1) > (a.dc ?? -1) ? b : a));
  }

  // --- combos ---------------------------------------------------------------
  const diagramCombos: CalcDiagramCombo[] = s.diagram?.combos ?? [];
  const govCombo =
    (s.governing && checksBySummaryKey.get(s.governing)?.governingCombo) ||
    s.diagram?.governingCombo ||
    gov?.combo ||
    null;
  const combos: string[] = diagramCombos.map((c) => c.name);

  // --- per-combination capacity tables (engine-authored, all combos): pure
  //     passthrough of structured.comboTables — no derivation. ----------------
  const comboTables: ResultComboTable[] = (s.comboTables ?? []).map((t) => ({
    key: t.key,
    label: t.label,
    demandSymbol: t.demandSymbol,
    capacitySymbol: t.capacitySymbol,
    rows: (t.rows ?? []).map((r) => ({
      combo: r.combo,
      cd: r.cd ?? null,
      lambdaFactor: r.lambdaFactor ?? null,
      designMethod: r.designMethod ?? null,
      factorBasis: r.factorBasis ?? null,
      demand: r.demand ?? null,
      capacity: r.capacity ?? null,
      units: r.units,
      dc: r.utilization ?? null,
      passes: !!r.passes,
      cl: r.cl ?? null,
      edge: r.edge ?? null,
      mcrLbft: r.mcrLbft ?? null,
    })),
  }));
  // per-combo DCR for the combo strip = the GOVERNING state's table (engine
  // value, not a client computation — the engine now emits per-combo DCR).
  const govDcByCombo = new Map<string, number | null>(
    (comboTables.find((t) => t.key === s.governing)?.rows ?? []).map((r) => [
      r.combo,
      r.dc,
    ]),
  );

  // axial calculators carry no moment metric → label by axial force when the
  // combo has no moment. Beam → moment in lb-ft.
  const isAxialResult = diagramCombos.some(
    (c) => (c.mMaxLbft === null || c.mMaxLbft === undefined),
  ) && diagramCombos.length > 0 && checks.some((c) => c.key === "compression" || c.key === "tension");

  const comboResults: ResultComboResult[] = diagramCombos.map((c) => ({
    label: c.name,
    dc: govDcByCombo.has(c.name) ? govDcByCombo.get(c.name) ?? null : null,
    metric: isAxialResult
      ? `${fmt(c.vMaxLb ?? c.mMaxLbft)} lb axial`
      : `${fmt(c.mMaxLbft)} lb·ft`,
    active: true,
    governs: c.name === govCombo,
  }));

  // --- factors: the engine emits the COMPLETE stack on structured.factors (the
  //     authoritative source). CD is read per-combo from the governing combo.
  //     The record-intermediates bag is a fallback only for payloads computed
  //     before structured.factors existed. INV-2 still holds: a factor the
  //     engine did not emit stays null, NEVER derived client-side. -------------
  const govComboObj = diagramCombos.find((c) => c.name === govCombo);
  const factorBySym = new Map<string, number | null>();
  for (const factor of s.factors ?? []) {
    // Some factors (notably Ci) are emitted once per affected property. The
    // scalar compatibility lookup keeps the first authoritative entry while
    // factorLedger below preserves every property-specific row.
    if (!factorBySym.has(factor.symbol)) {
      factorBySym.set(factor.symbol, factor.value);
    }
  }
  const ef = (sym: string, ...bagKeys: string[]): number | null =>
    factorBySym.has(sym)
      ? factorBySym.get(sym) ?? null
      : readFactor(bag, ...(bagKeys.length ? bagKeys : [sym]));
  const factors: ResultFactors = {
    CD: govComboObj?.cd ?? ef("CD"),
    CM: ef("CM"),
    Ct: ef("Ct"),
    Ci: ef("Ci"),
    Cfu: ef("Cfu"),
    Cr: ef("Cr"),
    CF: ef("CF"),
    CV: ef("CV"),
    CL: ef("CL"),
    Cb: ef("Cb"),
    CP: ef("CP"),
    slend: readFactor(bag, "le_over_d", "slend", "slenderness", "kL_d", "ke_l_over_d"),
    lam: ef("lambda", "lambda", "lam", "λ"),
  };
  const factorLedger: ResultFactor[] = (s.factors ?? []).map((factor) => ({
    symbol: factor.symbol,
    value: factor.value,
    kind: factor.kind ?? null,
    appliesTo: factor.appliesTo ?? [],
    basis: factor.basis ?? null,
    ndsRef: factor.ndsRef ?? null,
  }));

  const rawAxis = stringValue(recordInputs, "bending_axis") ?? req.vals.orientation ?? null;
  const criteria: ResultCriteria = {
    bendingAxis: rawAxis === "strong" || rawAxis === "weak" ? rawAxis : null,
    moistureService: stringValue(service, "moisture_service") ?? req.vals.service ?? null,
    temperatureClass: stringValue(service, "temperature_class") ?? req.vals.temp ?? null,
    incised: booleanValue(service, "incised") ?? req.vals.incised ?? null,
    compressionEdge: stringValue(bracing, "compression_edge") ?? req.vals.bracing ?? null,
    unbracedLengthIn: numericValue(bracing, "unbraced_length_in"),
    liveRatio: numericValue(serviceability, "live_ratio") ?? req.vals.deflLL ?? null,
    totalRatio: numericValue(serviceability, "total_ratio") ?? req.vals.deflTL ?? null,
    longTermRatio: numericValue(serviceability, "long_term_ratio") ?? req.vals.deflLT ?? null,
    absoluteCapIn: numericValue(serviceability, "absolute_cap_in") ?? req.vals.deflAbs ?? null,
    kcr: numericValue(serviceability, "kcr") ?? req.vals.kcr ?? null,
    includeShearDeformation: booleanValue(recordInputs, "include_shear_deformation")
      ?? req.vals.shearDeflection ?? null,
    shearMethod: stringValue(recordInputs, "shear_method"),
  };

  // --- adjusted (primed) stresses: from structured.adjustedStresses (engine-
  //     authored, CD=1/CL=1 base), bag fallback, else null. NEVER computed. ----
  const adj = s.adjustedStresses ?? null;
  const ea = (v: number | null | undefined, ...bagKeys: string[]): number | null =>
    v != null ? v : readFactor(bag, ...bagKeys);
  const refs: ResultRefs = {
    Fb_p: ea(adj?.fbPrime, "Fb_prime", "fb_prime", "Fb_p"),
    Fv_p: ea(adj?.fvPrime, "Fv_prime", "fv_prime", "Fv_p"),
    Fcp_p: ea(adj?.fcPerpPrime, "Fcp_prime", "fc_perp_prime", "Fcp_p"),
    Fc_p: ea(adj?.fcPrime, "Fc_prime", "fc_prime", "Fc_p"),
    Ft_p: ea(adj?.ftPrime, "Ft_prime", "ft_prime", "Ft_p"),
    E_p: ea(adj?.ePrime, "E_prime", "e_prime", "E_p"),
    Emin_p: ea(adj?.eminPrime, "Emin_prime", "emin_prime", "Emin_p"),
  };

  // --- diagram: SHAPE-ONLY curves; labeled maxima are the engine's (INV-1) ---
  const spanIn = s.diagram?.spanIn ?? null;
  const shape = shapeArrays(spanIn);
  const diagram: ResultDiagram = {
    model: s.diagram?.model ?? "simple_uniform",
    spanIn,
    // These arrays are SHAPE-ONLY — they exist to draw the curve geometry and
    // MUST NEVER be read as a labeled magnitude / DCR / factor (INV-1).
    xs: shape.xs,
    V: shape.V,
    M: shape.M,
    defl: shape.defl,
    // labeled maxima — ALWAYS from the engine, never the shape arrays:
    mMaxLbft: govComboObj?.mMaxLbft ?? null,
    vMaxLb: govComboObj?.vMaxLb ?? null,
    deflMaxIn: s.diagram?.deflMaxIn ?? null,
    combos: diagramCombos.map((combo) => ({
      name: combo.name,
      cd: combo.cd ?? null,
      lambdaFactor: combo.lambdaFactor ?? null,
      designMethod: combo.designMethod ?? null,
      wPlf: combo.wPlf ?? null,
      mMaxLbft: combo.mMaxLbft ?? null,
      vMaxLb: combo.vMaxLb ?? null,
      isUplift: !!combo.isUplift,
    })),
    // real engine curve + multi-bearing geometry (continuous / cantilever):
    // prefer these over the shape arrays when present.
    samples: s.diagram?.samples ?? null,
    geometry: s.diagram?.geometry ?? null,
  };

  // --- provenance (INV-3 + INV-4) ------------------------------------------
  const provenance: Provenance = {
    source: ref.source === "member_db" ? "member_db" : "direct",
    label: ref.source === "member_db" ? "Firmark member database" : "Typed hand-calc",
    edition: s.ndsEdition ?? null, // engine-authored at runtime (INV-4)
    method: s.designMethod === "LRFD" ? "LRFD" : "ASD",
  };

  return {
    b: ref.bIn ?? null,
    d: ref.dIn ?? null,
    A: ref.areaIn2 ?? null,
    S: ref.sx ?? null,
    I: ref.ix ?? null,
    Iy: ref.iy ?? null,
    Sy: ref.sy ?? null,
    plies: ref.plies ?? null,
    mat,
    referenceDetails: ref.details ?? {},
    checks,
    reactions,
    gov,
    govCombo,
    combos,
    comboResults,
    comboTables,
    factors,
    factorLedger,
    criteria,
    refs,
    diagram,
    provenance,
    assumptions: s.assumptions ?? [],
    warnings: s.warnings ?? [],
  };
}
